/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { LoadingSkeleton } from '@consentropy/shared';
import { AuthGuard } from '@consentropy/shared';
import { RoleGuard } from '@consentropy/shared';
import { AppShell } from '../components/layout/AppShell';
import { ROUTE_PATHS } from './routePaths';

const LoginPage = lazy(() => import('@consentropy/shared/features/auth/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@consentropy/shared/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@consentropy/shared/features/auth/pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage'));
const ProjectsPage = lazy(() => import('../features/projects/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('../features/projects/ProjectDetailPage'));
const ProjectArtifactsPage = lazy(() => import('../features/artifacts/ProjectArtifactsPage'));
const Level2ArtifactPage = lazy(() => import('../features/artifacts/Level2ArtifactPage'));
const DriftControlPage = lazy(() => import('../features/governance/DriftControlPage'));
const ProjectConversationsPage = lazy(() => import('../features/conversations/ProjectConversationsPage'));
const ConversationDetailPage = lazy(() => import('../features/conversations/ConversationDetailPage'));
const AIChatPage = lazy(() => import('../features/ai/AIChatPage'));
const KnowledgeQueryPage = lazy(() => import('../features/ai/KnowledgeQueryPage'));
const ProjectRegistersPage = lazy(() => import('../features/registers/ProjectRegistersPage'));
const AdminClientsPage = lazy(() => import('../features/admin/AdminClientsPage'));
const AdminUsersPage = lazy(() => import('../features/admin/AdminUsersPage'));
const ProfilePage = lazy(() => import('../features/profile/ProfilePage'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage'));
const NotFoundPage = lazy(() => import('@consentropy/shared/components/common/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@consentropy/shared/components/common/ForbiddenPage'));

function SuspenseWrapper({ children }: { children: ReactNode }) {
	return <Suspense fallback={<LoadingSkeleton />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
	{
		path: ROUTE_PATHS.LOGIN,
		element: (
			<SuspenseWrapper>
				<LoginPage />
			</SuspenseWrapper>
		),
	},
	{
		path: ROUTE_PATHS.FORGOT_PASSWORD,
		element: (
			<SuspenseWrapper>
				<ForgotPasswordPage />
			</SuspenseWrapper>
		),
	},
	{
		path: ROUTE_PATHS.RESET_PASSWORD,
		element: (
			<SuspenseWrapper>
				<ResetPasswordPage />
			</SuspenseWrapper>
		),
	},
	{
		element: <AuthGuard />,
		children: [
			{
				element: <AppShell />,
				children: [
					{
						path: ROUTE_PATHS.HOME,
						element: <Navigate to={ROUTE_PATHS.DASHBOARD} replace />,
					},
					{
						path: ROUTE_PATHS.DASHBOARD,
						element: (
							<SuspenseWrapper>
								<DashboardPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROJECTS,
						element: (
							<SuspenseWrapper>
								<ProjectsPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROJECT_DETAIL,
						element: (
							<SuspenseWrapper>
								<ProjectDetailPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROJECT_ARTIFACTS,
						element: (
							<SuspenseWrapper>
								<ProjectArtifactsPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROJECT_LEVEL2_ARTIFACT,
						element: (
							<SuspenseWrapper>
								<Level2ArtifactPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROJECT_DRIFT_CONTROL,
						element: (
							<SuspenseWrapper>
								<DriftControlPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROJECT_CONVERSATIONS,
						element: (
							<SuspenseWrapper>
								<ProjectConversationsPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROJECT_AI_CHAT,
						element: (
							<SuspenseWrapper>
								<AIChatPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROJECT_KNOWLEDGE,
						element: (
							<SuspenseWrapper>
								<KnowledgeQueryPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.CONVERSATION_DETAIL,
						element: (
							<SuspenseWrapper>
								<ConversationDetailPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROJECT_REGISTERS,
						element: (
							<SuspenseWrapper>
								<ProjectRegistersPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.PROFILE,
						element: (
							<SuspenseWrapper>
								<ProfilePage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.SETTINGS,
						element: (
							<SuspenseWrapper>
								<SettingsPage />
							</SuspenseWrapper>
						),
					},
					{
						path: ROUTE_PATHS.FORBIDDEN,
						element: (
							<SuspenseWrapper>
								<ForbiddenPage />
							</SuspenseWrapper>
						),
					},
					{
						element: <RoleGuard allowedRoles={['platform_admin']} />,
						children: [
							{
								path: ROUTE_PATHS.ADMIN_CLIENTS,
								element: (
									<SuspenseWrapper>
										<AdminClientsPage />
									</SuspenseWrapper>
								),
							},
							{
								path: ROUTE_PATHS.ADMIN_USERS,
								element: (
									<SuspenseWrapper>
										<AdminUsersPage />
									</SuspenseWrapper>
								),
							},
						],
					},
					{
						path: '*',
						element: (
							<SuspenseWrapper>
								<NotFoundPage />
							</SuspenseWrapper>
						),
					},
				],
			},
		],
	},
]);
